import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  CSidebar,
  CSidebarBrand,
  CSidebarNav,
  CSidebarMinimizer,
  CSidebarNavItem,
  CSidebarNavDropdown,
} from "@coreui/react";

import "./customSidebarStyle.css";
//import CIcon from '@coreui/icons-react';

import {
  minimizeDashboard,
  showDashboard,
} from "../../redux/actions/dashboard";
import { RootState } from "../../store";

// sidebar nav config
import { getRouteName, getRoutePath } from "../../routes";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import {
  ACCOUNTING_ACCOUNTS_RETRIEVE,
  ACCOUNTING_REPORTS_RETRIEVE,
  BATCH_AMOUNTS_RETRIEVE,
  CLIENTS_RETRIEVE,
  CLOTHING_MODELS_RETRIEVE,
  COLORS_RETRIEVE,
  DELIVERY_ORDERS_RETRIEVE,
  DISCOUNTS_RETRIEVE,
  INPUT_MATERIALS_HISTORY_RETRIEVE,
  INPUT_MATERIALS_RETRIEVE,
  INVENTORY_INPUT_BATCHES_RETRIEVE,
  INVENTORY_PRODUCTS_RETRIEVE,
  INVENTORY_TRANSFERS_RETRIEVE,
  PAYMENTS_RETRIEVE,
  PRODUCTION_RETRIEVE,
  PRODUCT_BRANDS_RETRIEVE,
  PRODUCT_HISTORY_RETRIEVE,
  SALE_ORDERS_RETRIEVE,
  SIZES_RETRIEVE,
  STORE_EXPENSES_RETRIEVE,
  STORE_SALES_RETRIEVE,
  SUPPLIERS_RETRIEVE,
  TREASURY_MANAGE,
  TREASURY_MOVEMENTS,
} from "../../auth/permissions";
import HasAnyPermissionComponent from "../permissions/HasAnyPermissionComponent";

// Route keys of every item inside each dropdown. A dropdown stays open while
// the current route is one of its own, so any item added to a group below must
// also be added here or its group will collapse when the item is selected.
const PRODUCTION_NAV_ROUTES = [
  "production",
  "barcodePrint",
  "products",
  "categories",
  "colors",
  "productBrands",
  "sizes",
];

const INVENTORY_NAV_ROUTES = [
  "inventoryProducts",
  "inventoryProductsFull",
  "inputMaterials",
  "inventoryInputBatches",
  "batchAmounts",
  "inventoryProductLogs",
  "inputMaterialLogs",
];

const SALES_NAV_ROUTES = [
  "saleOrders",
  "deliveryOrders",
  "payments",
  "saleOrderSummary",
];

const STORES_NAV_ROUTES = [
  "stores",
  "inventoryTransfers",
  "storeRequestList",
  "storeExpensesList",
  "discounts",
  "salesHistory",
  "storeExchanges",
  "cashRegisterHistory",
  "amountsSummary",
  "inventoryCategorySummary",
  "incomeExpenseSummary",
];

const ACCOUNTING_NAV_ROUTES = [
  "accountingReports",
  "accountingAccounts",
  "accountingEntries",
  "accountingReportsHistory",
];

const TREASURY_NAV_ROUTES = [
  "treasuryAccounts",
  "treasuryMovements",
  "treasuryAccountHistory",
];

const isCurrentNavGroup = (routeKeys: string[], pathname: string) => {
  return routeKeys.some((routeKey) => getRoutePath(routeKey) === pathname);
};

const Sidebar = () => {
  const dispatch = useDispatch();
  const show = useSelector((state: RootState) => state.dashboard.sidebarShow);
  const minimize = useSelector(
    (state: RootState) => state.dashboard.sidebarMinimize
  );

  const [showInventoryNav, setShowInventoryNav] = useState(
    isCurrentNavGroup(INVENTORY_NAV_ROUTES, window.location.pathname)
  );

  const [showProductionNav, setShowProductionNav] = useState(
    isCurrentNavGroup(PRODUCTION_NAV_ROUTES, window.location.pathname)
  );

  const [showSalesNav, setShowSalesNav] = useState(
    isCurrentNavGroup(SALES_NAV_ROUTES, window.location.pathname)
  );

  const [showStoresNav, setShowStoresNav] = useState(
    isCurrentNavGroup(STORES_NAV_ROUTES, window.location.pathname)
  );

  const [showAccountingNav, setShowAccountingNav] = useState(
    isCurrentNavGroup(ACCOUNTING_NAV_ROUTES, window.location.pathname)
  );

  const [showTreasuryNav, setShowTreasuryNav] = useState(
    isCurrentNavGroup(TREASURY_NAV_ROUTES, window.location.pathname)
  );

  const history = useHistory();

  useEffect(() => {
    return history.listen((location) => {
      setShowInventoryNav(
        isCurrentNavGroup(INVENTORY_NAV_ROUTES, location.pathname)
      );
      setShowProductionNav(
        isCurrentNavGroup(PRODUCTION_NAV_ROUTES, location.pathname)
      );
      setShowSalesNav(isCurrentNavGroup(SALES_NAV_ROUTES, location.pathname));
      setShowStoresNav(isCurrentNavGroup(STORES_NAV_ROUTES, location.pathname));
      setShowAccountingNav(
        isCurrentNavGroup(ACCOUNTING_NAV_ROUTES, location.pathname)
      );
      setShowTreasuryNav(
        isCurrentNavGroup(TREASURY_NAV_ROUTES, location.pathname)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  const pendingTransfersCount = useSelector(
    (state: RootState) => state.transfers.pendingTransfers
  );
  const pendingStoreRequestsCount = useSelector(
    (state: RootState) => state.transfers.pendingRequests
  );

  const badgeTrasfers: object | undefined = pendingTransfersCount
    ? { color: "danger", text: pendingTransfersCount }
    : undefined;

  const badgeStoreRequests: object | undefined = pendingStoreRequestsCount
    ? { color: "danger", text: pendingStoreRequestsCount }
    : undefined;

  return (
    <CSidebar
      show={show}
      onShowChange={(val: boolean) => {
        dispatch(showDashboard(val));
        window.localStorage.setItem("sidebarShow", val ? "true" : "false");
      }}
      minimize={minimize}
      onMinimizeChange={(val: boolean) => {
        dispatch(minimizeDashboard(!val));
        window.localStorage.setItem("sidebarMinimize", !val ? "true" : "false");
      }}
      dropdownMode={"noAction"}
    >
      <CSidebarBrand className="d-md-down-none" to="/"></CSidebarBrand>
      <CSidebarNav>
        <CSidebarNavItem
          name={getRouteName("dashboard")}
          to={getRoutePath("dashboard")}
          icon={"cil-home"}
        ></CSidebarNavItem>
        <PermissionRequiredComponent permissionName={SUPPLIERS_RETRIEVE}>
          <CSidebarNavItem
            name={getRouteName("suppliers")}
            to={getRoutePath("suppliers")}
            icon={<i className="c-sidebar-nav-icon fa fa-truck"></i>}
          ></CSidebarNavItem>
        </PermissionRequiredComponent>
        <PermissionRequiredComponent permissionName={CLIENTS_RETRIEVE}>
          <CSidebarNavItem
            name={getRouteName("clients")}
            to={getRoutePath("clients")}
            icon={<i className="c-sidebar-nav-icon fa fa-person"></i>}
          ></CSidebarNavItem>
        </PermissionRequiredComponent>

        <HasAnyPermissionComponent
          permissionNames={[
            PRODUCTION_RETRIEVE,
            CLOTHING_MODELS_RETRIEVE,
            COLORS_RETRIEVE,
            PRODUCT_BRANDS_RETRIEVE,
            SIZES_RETRIEVE,
          ]}
        >
          <CSidebarNavDropdown
            name="Producción"
            icon={<i className="c-sidebar-nav-icon fa fa-industry"></i>}
            show={showProductionNav}
          >
            <PermissionRequiredComponent permissionName={PRODUCTION_RETRIEVE}>
              <CSidebarNavItem
                name={getRouteName("production")}
                to={getRoutePath("production")}
                icon={<i className="c-sidebar-nav-icon fa fa-scissors"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent
              permissionName={CLOTHING_MODELS_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("barcodePrint")}
                to={getRoutePath("barcodePrint")}
                icon={<i className="c-sidebar-nav-icon fa fa-barcode"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent
              permissionName={CLOTHING_MODELS_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("products")}
                to={getRoutePath("products")}
                icon={<i className="c-sidebar-nav-icon fa fa-shirt"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent
              permissionName={CLOTHING_MODELS_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("categories")}
                to={getRoutePath("categories")}
                icon={<i className="c-sidebar-nav-icon fa fa-layer-group"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent permissionName={COLORS_RETRIEVE}>
              <CSidebarNavItem
                name={getRouteName("colors")}
                to={getRoutePath("colors")}
                icon={<i className="c-sidebar-nav-icon fa fa-droplet"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent
              permissionName={PRODUCT_BRANDS_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("productBrands")}
                to={getRoutePath("productBrands")}
                icon={<i className="c-sidebar-nav-icon fa fa-tag"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent permissionName={SIZES_RETRIEVE}>
              <CSidebarNavItem
                name={getRouteName("sizes")}
                to={getRoutePath("sizes")}
                icon={
                  <i className="c-sidebar-nav-icon fa fa-up-right-and-down-left-from-center"></i>
                }
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
          </CSidebarNavDropdown>
        </HasAnyPermissionComponent>
        <HasAnyPermissionComponent
          permissionNames={[
            INVENTORY_PRODUCTS_RETRIEVE,
            INPUT_MATERIALS_RETRIEVE,
            INVENTORY_INPUT_BATCHES_RETRIEVE,
          ]}
        >
          <CSidebarNavDropdown
            name="Stock Depósito"
            icon={<i className="c-sidebar-nav-icon fa fa-boxes-stacked"></i>}
            show={showInventoryNav}
          >
            <PermissionRequiredComponent
              permissionName={INVENTORY_PRODUCTS_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("inventoryProducts")}
                to={getRoutePath("inventoryProducts")}
                icon={<i className="c-sidebar-nav-icon fa fa-shirt"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent
              permissionName={INVENTORY_PRODUCTS_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("inventoryProductsFull")}
                to={getRoutePath("inventoryProductsFull")}
                icon={<i className="c-sidebar-nav-icon fa fa-box"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent
              permissionName={INPUT_MATERIALS_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("inputMaterials")}
                to={getRoutePath("inputMaterials")}
                icon={"cil-layers"}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent
              permissionName={INVENTORY_INPUT_BATCHES_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("inventoryInputBatches")}
                to={getRoutePath("inventoryInputBatches")}
                icon={<i className="c-sidebar-nav-icon fa fa-list"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent
              permissionName={BATCH_AMOUNTS_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("batchAmounts")}
                to={getRoutePath("batchAmounts")}
                icon={<i className="c-sidebar-nav-icon fa fa-chart-line"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>

            <PermissionRequiredComponent
              permissionName={PRODUCT_HISTORY_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("inventoryProductLogs")}
                to={getRoutePath("inventoryProductLogs")}
                icon={
                  <i className="c-sidebar-nav-icon fa fa-clock-rotate-left"></i>
                }
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent
              permissionName={INPUT_MATERIALS_HISTORY_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("inputMaterialLogs")}
                to={getRoutePath("inputMaterialLogs")}
                icon={
                  <i className="c-sidebar-nav-icon fa fa-clock-rotate-left"></i>
                }
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
          </CSidebarNavDropdown>
        </HasAnyPermissionComponent>
        <HasAnyPermissionComponent
          permissionNames={[
            SALE_ORDERS_RETRIEVE,
            DELIVERY_ORDERS_RETRIEVE,
            PAYMENTS_RETRIEVE,
          ]}
        >
          <CSidebarNavDropdown
            name="Ventas"
            icon={<i className="c-sidebar-nav-icon fa fa-dollar"></i>}
            show={showSalesNav}
          >
            <PermissionRequiredComponent permissionName={SALE_ORDERS_RETRIEVE}>
              <CSidebarNavItem
                name={getRouteName("saleOrders")}
                to={getRoutePath("saleOrders")}
                icon={<i className="c-sidebar-nav-icon fa fa-coins"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent
              permissionName={DELIVERY_ORDERS_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("deliveryOrders")}
                to={getRoutePath("deliveryOrders")}
                icon={
                  <i className="c-sidebar-nav-icon fa fa-truck-ramp-box"></i>
                }
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent permissionName={PAYMENTS_RETRIEVE}>
              <CSidebarNavItem
                name={getRouteName("payments")}
                to={getRoutePath("payments")}
                icon={<i className="c-sidebar-nav-icon fa fa-money-bill"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent permissionName={SALE_ORDERS_RETRIEVE}>
              <CSidebarNavItem
                name={getRouteName("saleOrderSummary")}
                to={getRoutePath("saleOrderSummary")}
                icon={<i className="c-sidebar-nav-icon fa fa-layer-group"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
          </CSidebarNavDropdown>
        </HasAnyPermissionComponent>
        <HasAnyPermissionComponent
          permissionNames={[
            INVENTORY_TRANSFERS_RETRIEVE,
            STORE_SALES_RETRIEVE,
            DISCOUNTS_RETRIEVE,
            INVENTORY_PRODUCTS_RETRIEVE,
            STORE_EXPENSES_RETRIEVE,
          ]}
        >
          <CSidebarNavDropdown
            name="Gestion de Tiendas"
            icon={<i className="c-sidebar-nav-icon fa fa-cash-register"></i>}
            show={showStoresNav}
          >
            <PermissionRequiredComponent
              permissionName={INVENTORY_PRODUCTS_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("stores")}
                to={getRoutePath("stores")}
                icon={<i className="c-sidebar-nav-icon fa fa-store"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent
              permissionName={INVENTORY_TRANSFERS_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("inventoryTransfers")}
                to={getRoutePath("inventoryTransfers")}
                icon={
                  <i className="c-sidebar-nav-icon fa fa-truck-ramp-box"></i>
                }
                badge={badgeTrasfers}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent
              permissionName={INVENTORY_TRANSFERS_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("storeRequestList")}
                to={getRoutePath("storeRequestList")}
                icon={
                  <i className="c-sidebar-nav-icon fa fa-tent-arrow-left-right"></i>
                }
                badge={badgeStoreRequests}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent
              permissionName={STORE_EXPENSES_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("storeExpensesList")}
                to={getRoutePath("storeExpensesList")}
                icon={<i className="c-sidebar-nav-icon fa fa-wallet"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent permissionName={DISCOUNTS_RETRIEVE}>
              <CSidebarNavItem
                name={getRouteName("discounts")}
                to={getRoutePath("discounts")}
                icon={<i className="c-sidebar-nav-icon fa fa-tag"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent permissionName={STORE_SALES_RETRIEVE}>
              <CSidebarNavItem
                name={getRouteName("salesHistory")}
                to={getRoutePath("salesHistory")}
                icon={<i className="c-sidebar-nav-icon fa fa-money-bill"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent permissionName={STORE_SALES_RETRIEVE}>
              <CSidebarNavItem
                name={getRouteName("storeExchanges")}
                to={getRoutePath("storeExchanges")}
                icon={
                  <i className="c-sidebar-nav-icon fa fa-right-left"></i>
                }
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent permissionName={STORE_SALES_RETRIEVE}>
              <CSidebarNavItem
                name={getRouteName("cashRegisterHistory")}
                to={getRoutePath("cashRegisterHistory")}
                icon={
                  <i className="c-sidebar-nav-icon fa fa-cash-register"></i>
                }
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent permissionName={STORE_SALES_RETRIEVE}>
              <CSidebarNavItem
                name={getRouteName("amountsSummary")}
                to={getRoutePath("amountsSummary")}
                icon={
                  <i className="c-sidebar-nav-icon fa fa-cash-register"></i>
                }
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent permissionName={STORE_SALES_RETRIEVE}>
              <CSidebarNavItem
                name={getRouteName("inventoryCategorySummary")}
                to={getRoutePath("inventoryCategorySummary")}
                icon={<i className="c-sidebar-nav-icon fa fa-layer-group"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent permissionName={STORE_SALES_RETRIEVE}>
              <CSidebarNavItem
                name={getRouteName("incomeExpenseSummary")}
                to={getRoutePath("incomeExpenseSummary")}
                icon={<i className="c-sidebar-nav-icon fa fa-star"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
          </CSidebarNavDropdown>
        </HasAnyPermissionComponent>
        <HasAnyPermissionComponent
          permissionNames={[
            ACCOUNTING_REPORTS_RETRIEVE,
            ACCOUNTING_ACCOUNTS_RETRIEVE,
          ]}
        >
          <CSidebarNavDropdown
            name="Contabilidad"
            icon={<i className="c-sidebar-nav-icon fa fa-calculator"></i>}
            show={showAccountingNav}
          >
            <PermissionRequiredComponent
              permissionName={ACCOUNTING_REPORTS_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("accountingReports")}
                to={getRoutePath("accountingReports")}
                icon={<i className="c-sidebar-nav-icon fa fa-chart-line"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent
              permissionName={ACCOUNTING_ACCOUNTS_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("accountingAccounts")}
                to={getRoutePath("accountingAccounts")}
                icon={<i className="c-sidebar-nav-icon fa fa-list"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent
              permissionName={ACCOUNTING_ACCOUNTS_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("accountingEntries")}
                to={getRoutePath("accountingEntries")}
                icon={<i className="c-sidebar-nav-icon fa fa-list"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent
              permissionName={ACCOUNTING_ACCOUNTS_RETRIEVE}
            >
              <CSidebarNavItem
                name={getRouteName("accountingReportsHistory")}
                to={getRoutePath("accountingReportsHistory")}
                icon={<i className="c-sidebar-nav-icon fa fa-list"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
          </CSidebarNavDropdown>
        </HasAnyPermissionComponent>
        <HasAnyPermissionComponent
          permissionNames={[TREASURY_MANAGE, TREASURY_MOVEMENTS]}
        >
          <CSidebarNavDropdown
            name="Estado de Cuentas"
            icon={<i className="c-sidebar-nav-icon fa fa-university"></i>}
            show={showTreasuryNav}
          >
            <PermissionRequiredComponent permissionName={TREASURY_MANAGE}>
              <CSidebarNavItem
                name={getRouteName("treasuryAccounts")}
                to={getRoutePath("treasuryAccounts")}
                icon={<i className="c-sidebar-nav-icon fa fa-wallet"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
            <PermissionRequiredComponent permissionName={TREASURY_MOVEMENTS}>
              <CSidebarNavItem
                name={getRouteName("treasuryMovements")}
                to={getRoutePath("treasuryMovements")}
                icon={<i className="c-sidebar-nav-icon fa fa-inbox"></i>}
              ></CSidebarNavItem>
            </PermissionRequiredComponent>
          </CSidebarNavDropdown>
        </HasAnyPermissionComponent>
        <CSidebarNavItem
          name={getRouteName("users")}
          to={getRoutePath("users")}
          icon={"cil-people"}
        ></CSidebarNavItem>
      </CSidebarNav>
      <CSidebarMinimizer className="c-d-md-down-none" />
    </CSidebar>
  );
};

export default Sidebar;
